import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  Eye,
  EyeOff,
  Lock,
  User,
  AlertCircle,
  Globe,
  Shield,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

/* ── Inline SVG illustrations ── */
const PhonePaymentSvg = () => (
  <svg viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Phone body */}
    <rect x="50" y="10" width="100" height="200" rx="16" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
    <rect x="58" y="30" width="84" height="150" rx="4" fill="white"/>
    {/* Screen content - money transfer */}
    <circle cx="100" cy="70" r="18" fill="#d1fae5"/>
    <path d="M92 70l6 6 12-12" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="72" y="100" width="56" height="6" rx="3" fill="#e5e7eb"/>
    <rect x="80" y="112" width="40" height="4" rx="2" fill="#f3f4f6"/>
    {/* Money flying */}
    <rect x="140" y="50" width="30" height="18" rx="3" fill="#a7f3d0" stroke="#10b981" strokeWidth="1.5" transform="rotate(-15 140 50)"/>
    <text x="148" y="63" fontSize="8" fill="#059669" fontWeight="bold" transform="rotate(-15 140 50)">$</text>
    <rect x="150" y="35" width="26" height="16" rx="3" fill="#bbf7d0" stroke="#10b981" strokeWidth="1" transform="rotate(-25 150 35)"/>
    {/* Signal waves */}
    <path d="M30 80 Q25 70 30 60" stroke="#6366f1" strokeWidth="1.5" fill="none" opacity="0.4"/>
    <path d="M24 85 Q17 70 24 55" stroke="#6366f1" strokeWidth="1.5" fill="none" opacity="0.25"/>
    {/* Notch */}
    <rect x="85" y="14" width="30" height="6" rx="3" fill="#c7d2fe"/>
  </svg>
);

const CardPaymentSvg = () => (
  <svg viewBox="0 0 220 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Card */}
    <rect x="20" y="20" width="160" height="100" rx="12" fill="url(#cardGrad)" stroke="#4f46e5" strokeWidth="1.5"/>
    <defs>
      <linearGradient id="cardGrad" x1="20" y1="20" x2="180" y2="120" gradientUnits="userSpaceOnUse">
        <stop stopColor="#667eea"/>
        <stop offset="1" stopColor="#5a67d8"/>
      </linearGradient>
    </defs>
    {/* Chip */}
    <rect x="40" y="45" width="28" height="20" rx="4" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1"/>
    <line x1="48" y1="45" x2="48" y2="65" stroke="#f59e0b" strokeWidth="0.5"/>
    <line x1="56" y1="45" x2="56" y2="65" stroke="#f59e0b" strokeWidth="0.5"/>
    <line x1="40" y1="55" x2="68" y2="55" stroke="#f59e0b" strokeWidth="0.5"/>
    {/* Card number dots */}
    {[0,1,2,3].map(i => (
      <g key={i}>
        {[0,1,2,3].map(j => (
          <circle key={j} cx={42 + i * 36 + j * 8} cy="82" r="2.5" fill="white" opacity="0.7"/>
        ))}
      </g>
    ))}
    {/* Contactless waves */}
    <path d="M150 42 Q156 48 150 54" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <path d="M154 39 Q162 48 154 57" stroke="white" strokeWidth="1.5" fill="none" opacity="0.4"/>
    <path d="M158 36 Q168 48 158 60" stroke="white" strokeWidth="1.5" fill="none" opacity="0.25"/>
    {/* Sparkles around card */}
    <circle cx="190" cy="30" r="3" fill="#a7f3d0"/>
    <circle cx="10" cy="90" r="2" fill="#c7d2fe"/>
    <circle cx="195" cy="110" r="4" fill="#fde68a"/>
    {/* Arrow showing tap */}
    <path d="M120 130 L120 125 L115 130 L120 125 L125 130" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
    <path d="M120 140 L120 128" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2" opacity="0.3"/>
  </svg>
);

const TransferPersonSvg = () => (
  <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Person 1 */}
    <circle cx="55" cy="60" r="18" fill="#dbeafe" stroke="#6366f1" strokeWidth="1.5"/>
    <circle cx="55" cy="55" r="7" fill="#93c5fd"/>
    <path d="M42 72 Q55 82 68 72" stroke="#93c5fd" strokeWidth="2" fill="#bfdbfe"/>
    {/* Phone in hand */}
    <rect x="40" y="90" width="22" height="36" rx="4" fill="#e0e7ff" stroke="#6366f1" strokeWidth="1.5"/>
    <rect x="44" y="96" width="14" height="22" rx="2" fill="white"/>
    {/* Arrow / transfer flow */}
    <path d="M80 108 L120 108" stroke="#10b981" strokeWidth="2" strokeLinecap="round"/>
    <path d="M115 103 L122 108 L115 113" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Money symbols flowing */}
    <circle cx="90" cy="100" r="6" fill="#d1fae5" stroke="#10b981" strokeWidth="1"/>
    <text x="87" y="104" fontSize="8" fill="#059669" fontWeight="bold">$</text>
    <circle cx="105" cy="98" r="5" fill="#d1fae5" stroke="#10b981" strokeWidth="1"/>
    <text x="102.5" y="101.5" fontSize="7" fill="#059669" fontWeight="bold">$</text>
    {/* Person 2 */}
    <circle cx="145" cy="60" r="18" fill="#fce7f3" stroke="#ec4899" strokeWidth="1.5"/>
    <circle cx="145" cy="55" r="7" fill="#f9a8d4"/>
    <path d="M132 72 Q145 82 158 72" stroke="#f9a8d4" strokeWidth="2" fill="#fbcfe8"/>
    {/* Phone in hand */}
    <rect x="138" y="90" width="22" height="36" rx="4" fill="#fce7f3" stroke="#ec4899" strokeWidth="1.5"/>
    <rect x="142" y="96" width="14" height="22" rx="2" fill="white"/>
    <circle cx="149" cy="107" r="4" fill="#d1fae5"/>
    <path d="M146 107 l2.5 2.5 4-4" stroke="#10b981" strokeWidth="1.2" strokeLinecap="round"/>
    {/* Decorative dots */}
    <circle cx="100" cy="150" r="2" fill="#c7d2fe" opacity="0.5"/>
    <circle cx="85" cy="145" r="1.5" fill="#a7f3d0" opacity="0.5"/>
    <circle cx="115" cy="148" r="1.5" fill="#fde68a" opacity="0.5"/>
  </svg>
);

const MobileWalletSvg = () => (
  <svg viewBox="0 0 180 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Wallet */}
    <rect x="30" y="60" width="120" height="80" rx="10" fill="#f0fdf4" stroke="#10b981" strokeWidth="1.5"/>
    <rect x="30" y="60" width="120" height="25" rx="10" fill="#d1fae5"/>
    <rect x="110" y="90" width="30" height="20" rx="6" fill="#10b981"/>
    <circle cx="125" cy="100" r="5" fill="#a7f3d0"/>
    {/* Bills sticking out */}
    <rect x="45" y="50" width="60" height="20" rx="4" fill="#bbf7d0" stroke="#10b981" strokeWidth="1" transform="rotate(-3 45 50)"/>
    <rect x="50" y="45" width="55" height="18" rx="4" fill="#d1fae5" stroke="#10b981" strokeWidth="1" transform="rotate(2 50 45)"/>
    {/* Coins */}
    <circle cx="50" cy="160" r="12" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
    <text x="46" y="164" fontSize="10" fill="#b45309" fontWeight="bold">$</text>
    <circle cx="75" cy="165" r="10" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1"/>
    <text x="72" y="169" fontSize="8" fill="#b45309" fontWeight="bold">$</text>
    <circle cx="95" cy="162" r="8" fill="#fde68a" stroke="#f59e0b" strokeWidth="1"/>
    {/* Upward arrows */}
    <path d="M140 55 L140 30" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" opacity="0.4"/>
    <path d="M135 35 L140 28 L145 35" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4"/>
    <path d="M155 50 L155 35" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" opacity="0.3"/>
    <path d="M150 40 L155 33 L160 40" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
  </svg>
);

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLocalization();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const success = await login(formData.username, formData.password);
      if (success) {
        navigate("/dashboard");
      } else {
        setError(t("login.errorInvalid"));
      }
    } catch {
      setError(t("login.errorGeneric"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      {/* Background decorative dots */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(99,102,241,0.4) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-2.5">
          <img src="/LOGO_MOKO_fd_vert.png" alt="MOKO Afrika" className="h-10 w-10 rounded-xl shadow-md object-cover"/>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900 leading-tight">MOKO Afrika</span>
            <span className="text-[9px] text-indigo-500 font-bold tracking-[0.15em] uppercase">Merchant Suite</span>
          </div>
        </div>

        {/* Language selector */}
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white shadow-sm">
          <Globe className="w-4 h-4 text-slate-500" />
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as "fr" | "en")}
            className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none cursor-pointer"
          >
            <option value="fr">Francais</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 pb-10">
        {/* Illustrations - positioned around the form */}
        <div className="hidden lg:block absolute left-[5%] top-[10%] w-48 h-52 opacity-60 hover:opacity-90 transition-opacity duration-500">
          <PhonePaymentSvg />
        </div>
        <div className="hidden lg:block absolute right-[5%] top-[8%] w-52 h-40 opacity-60 hover:opacity-90 transition-opacity duration-500">
          <CardPaymentSvg />
        </div>
        <div className="hidden lg:block absolute left-[8%] bottom-[8%] w-48 h-48 opacity-50 hover:opacity-80 transition-opacity duration-500">
          <TransferPersonSvg />
        </div>
        <div className="hidden lg:block absolute right-[8%] bottom-[10%] w-44 h-48 opacity-50 hover:opacity-80 transition-opacity duration-500">
          <MobileWalletSvg />
        </div>

        {/* Centered form card */}
        <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1 mb-5">
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
              <span className="text-[11px] font-semibold text-indigo-700 tracking-wide uppercase">
                {t("login.secureAccess") || "Secure access"}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 tracking-tight">
              {t("login.welcome") || "Welcome"}
            </h1>
            <p className="text-slate-500 text-sm">
              {t("login.subtitle") || "Sign in to your merchant dashboard"}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 login-shake">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-3.5 w-3.5 text-red-600" />
              </div>
              <span className="text-sm text-red-700 leading-snug">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-2">
                {t("login.username") || "Username"}
              </label>
              <div className="relative group">
                <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                  <User className="w-[18px] h-[18px] text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                  placeholder={t("login.usernamePlaceholder") || "Enter your username"}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[13px] font-semibold text-slate-700">
                  {t("login.password") || "Password"}
                </label>
                <button type="button" onClick={() => navigate("/forgot-password")} className="text-[12px] font-semibold text-indigo-600 hover:text-indigo-700 transition-colors" tabIndex={-1}>
                  {t("login.forgotPassword") || "Forgot password?"}
                </button>
              </div>
              <div className="relative group">
                <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                  <Lock className="w-[18px] h-[18px] text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                  placeholder={t("login.passwordPlaceholder") || "Enter your password"}
                  required
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <div className="relative">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="peer sr-only" />
                  <div className="w-[18px] h-[18px] rounded-md border-2 border-slate-300 peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-all flex items-center justify-center group-hover:border-indigo-400">
                    <CheckCircle2 className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" />
                  </div>
                </div>
                <span className="text-[13px] text-slate-600 font-medium select-none">
                  {t("login.rememberMe") || "Keep me signed in"}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative py-3.5 rounded-xl font-semibold text-[15px] text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2 group overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
            >
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 relative" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="relative">{t("login.signingIn") || "Signing in..."}</span>
                </>
              ) : (
                <>
                  <span className="relative">{t("login.signin") || "Sign In"}</span>
                  <ArrowRight className="w-4 h-4 relative group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-400">
            <Shield className="w-3 h-3" />
            <span>{t("login.encrypted") || "SSL Encrypted"}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-5 text-xs text-slate-400">
        &copy; {new Date().getFullYear()} MOKO Afrika. {t("login.allRightsReserved") || "All rights reserved."}
      </div>
    </div>
  );
};

export default Login;
